// File: src/components/Admin/InternetAvailabilityChecker.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ShoppingCart as CartIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface AvailabilityResult {
  type: 'VDSL' | 'Fibre' | 'Cable Internet';
  available: boolean;
  speeds_available?: string[]; // For VDSL only
  matched_addon_id: number | null;
  matched_addon_name: string | null;
  our_price: number | null;
  setup_fee: number | null;
}

interface InternetAvailabilityCheckerProps {
  open: boolean;
  onClose: () => void;
  onSelectService?: (serviceId: number) => void;
}

const InternetAvailabilityChecker: React.FC<InternetAvailabilityCheckerProps> = ({
  open,
  onClose,
  onSelectService,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<AvailabilityResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  
  const [formData, setFormData] = useState({
    street_number: '',
    street_name: '',
    street_suffix: '',
    street_direction: '',
    city: '',
    province: 'ON',
    postal_code: '',
  });
  
  const [clarificationData, setClarificationData] = useState<any>(null);
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [dynamicSuffixes, setDynamicSuffixes] = useState<any[]>([]);
  const [loadingSuffixes, setLoadingSuffixes] = useState(false);
  
  // Default street suffix options (may be overridden by dynamic loading)
  const defaultStreetSuffixes = [
    { value: '', label: 'Enter street name first...' },
  ];
  
  // Street direction options - matching Fibernetics dropdown exactly
  const streetDirections = [
    { value: '', label: '' },  // Empty option
    { value: 'E', label: 'E' },
    { value: 'W', label: 'W' },
    { value: 'N', label: 'N' },
    { value: 'NW', label: 'NW' },
    { value: 'NE', label: 'NE' },
    { value: 'S', label: 'S' },
    { value: 'SW', label: 'SW' },
    { value: 'SE', label: 'SE' },
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // When street name changes, clear suffix and fetch new options
    if (field === 'street_name' && value.length >= 3 && formData.street_number) {
      fetchStreetSuffixes();
    }
  };
  
  const fetchStreetSuffixes = async () => {
    if (!formData.street_number || !formData.street_name) return;
    
    setLoadingSuffixes(true);
    try {
      const response = await api.post('/endpoints/check-street-suffixes', {
        street_number: formData.street_number,
        street_name: formData.street_name,
        city: formData.city,
        province: formData.province,
      });
      
      if (response.data.success && response.data.data.suffixes) {
        setDynamicSuffixes(response.data.data.suffixes);
        // If only one suffix, auto-select it
        if (response.data.data.suffixes.length === 1) {
          setFormData(prev => ({ ...prev, street_suffix: response.data.data.suffixes[0].value }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch street suffixes:', err);
    } finally {
      setLoadingSuffixes(false);
    }
  };

  const handleSearch = async () => {
    if (!formData.street_number.trim() || !formData.street_name.trim()) {
      setError('Please enter street number and name');
      return;
    }
    
    if (!formData.street_suffix) {
      setError('Please select a street suffix');
      return;
    }
    
    if (!formData.postal_code.trim()) {
      setError('Please enter a postal code');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(false);

    try {
      // Combine address parts for the API
      const requestData = {
        address: `${formData.street_number} ${formData.street_name} ${formData.street_suffix}${formData.street_direction ? ' ' + formData.street_direction : ''}`,
        city: formData.city,
        province: formData.province,
        postal_code: formData.postal_code,
      };
      
      const response = await api.post('/endpoints/check-internet-availability', requestData);
      
      if (response.data.success) {
        // Check if clarification is needed
        if (response.data.data.requires_clarification) {
          setClarificationData(response.data.data);
          setError('Please select your specific unit from the options below');
        } else {
          setResults(response.data.data.services || []);
          setHasSearched(true);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to check availability');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectService = (service: AvailabilityResult) => {
    if (service.matched_addon_id && onSelectService) {
      onSelectService(service.matched_addon_id);
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({
      street_number: '',
      street_name: '',
      street_suffix: '',
      street_direction: '',
      city: '',
      province: 'ON',
      postal_code: '',
    });
    setResults([]);
    setHasSearched(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SearchIcon />
          Check Internet Service Availability
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Enter the service address to check available internet plans
            </Alert>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Street Number"
              value={formData.street_number}
              onChange={(e) => handleInputChange('street_number', e.target.value)}
              placeholder="123"
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Street Name"
              value={formData.street_name}
              onChange={(e) => handleInputChange('street_name', e.target.value)}
              placeholder="Main"
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Street Suffix"
              value={formData.street_suffix}
              onChange={(e) => handleInputChange('street_suffix', e.target.value)}
              select
              SelectProps={{ native: true }}
              required
              disabled={loadingSuffixes || (!formData.street_number || !formData.street_name)}
              helperText={loadingSuffixes ? 'Loading...' : ''}
            >
              {(dynamicSuffixes.length > 0 ? dynamicSuffixes : defaultStreetSuffixes).map(suffix => (
                <option key={suffix.value} value={suffix.value}>
                  {suffix.label}
                </option>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={1}>
            <TextField
              fullWidth
              label="Direction"
              value={formData.street_direction}
              onChange={(e) => handleInputChange('street_direction', e.target.value)}
              select
              SelectProps={{ native: true }}
            >
              {streetDirections.map(dir => (
                <option key={dir.value} value={dir.value}>
                  {dir.label}
                </option>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Toronto"
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Province"
              value={formData.province}
              onChange={(e) => handleInputChange('province', e.target.value)}
              select
              SelectProps={{ native: true }}
            >
              <option value="ON">ON</option>
              <option value="QC">QC</option>
              <option value="BC">BC</option>
              <option value="AB">AB</option>
              <option value="MB">MB</option>
              <option value="SK">SK</option>
              <option value="NS">NS</option>
              <option value="NB">NB</option>
              <option value="NL">NL</option>
              <option value="PE">PE</option>
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value.toUpperCase())}
              placeholder="M5V 3A8"
              required
              helperText="Required for availability check"
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Grid>
          )}

          {loading && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Checking availability...
                </Typography>
              </Box>
            </Grid>
          )}

          {!loading && hasSearched && results.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                No internet services found for this address.
              </Alert>
            </Grid>
          )}

          {!loading && clarificationData && clarificationData.units && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Bell Nexia - Please Clarify Address
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Multiple units found at this address. Please select your specific unit:
              </Alert>
              <List>
                {clarificationData.units.map((unit: any, index: number) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={unit.full_address}
                      secondary={`Unit ${unit.unit || ''}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          setSelectedUnit(unit.full_address);
                          // TODO: Submit unit selection
                        }}
                      >
                        Select
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}

          {!loading && results.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Service Availability
              </Typography>
              <List>
                {results.map((service, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {service.available ? (
                              <CheckIcon color="success" />
                            ) : (
                              <CancelIcon color="error" />
                            )}
                            <Typography variant="subtitle1">
                              {service.type}
                            </Typography>
                            {((service.type === 'VDSL' || service.type === 'Cable Internet') && service.speeds_available && service.speeds_available.length > 0) && (
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {service.speeds_available.map((speed) => (
                                  <Chip
                                    key={speed}
                                    icon={<SpeedIcon />}
                                    label={speed}
                                    size="small"
                                    color="primary"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box mt={1}>
                            <Typography variant="body2" color="text.secondary">
                              Status: {service.available ? 'Available' : 'Not Available'}
                            </Typography>
                            {service.matched_addon_id && (
                              <Typography variant="body2" color="primary">
                                Our Service: {service.matched_addon_name} - 
                                ${service.our_price?.toFixed(2)}/month
                                {service.setup_fee && service.setup_fee > 0 && (
                                  <span> + ${service.setup_fee.toFixed(2)} setup</span>
                                )}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        {service.available && service.matched_addon_id && (
                          <IconButton
                            edge="end"
                            color="primary"
                            onClick={() => handleSelectService(service)}
                            title="Select this service"
                          >
                            <CartIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < results.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleSearch}
          variant="contained"
          startIcon={<SearchIcon />}
          disabled={loading || !formData.street_number.trim() || !formData.street_name.trim() || !formData.street_suffix || !formData.postal_code.trim()}
        >
          Check Availability
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternetAvailabilityChecker;